import {
  useClient,
  useUpdateClient,
  useCreateInvestmentAccount,
  useInvalidateClients,
} from "creso-core/hooks";
import { UpdateClientSchema } from "creso-core/zod";
import {
  Button,
  cn,
  DialogFooter,
  Form,
  FormField,
  FormInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  V2Label,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { Avatar, ClientStatusBadge, FormCountryCodePhoneInput } from "web-core";
import { getAccountType, getInitials, isMinor, isNRI } from "creso-core/utils";
import { ACCOUNT_TYPE_OPTIONS } from "creso-core/constants";
import { Activity, useCallback, useState } from "react";
import { InfoIcon, Loader2Icon } from "lucide-react";
import { useCheckKyc } from "@crm/hooks/onboarding";
import { AddClientDialogActions } from "./add-client-dialog";
import { KycDescription, KycStatusBadge } from "./kyc-status";
import { MinorKycFields } from "./minor-kyc-fields";

export function ClientLeadAccountUpdateForm({
  id,
  action,
  onClose,
}: {
  action?: AddClientDialogActions;
  id: number | string;
  onClose?: () => void;
}) {
  const { data: client } = useClient(Number(id));
  const isMinorAccount =
    action === "create-account" && client?.tax_status === "on_behalf_of_minor";
  const [isGuardianVerified, setIsGuardianVerified] = useState(false);

  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();

  const [cachedClientName, setCachedClientName] = useState<string | null>(null);

  const form = useForm({
    values: {
      name: client?.name ?? "",
      pan: client?.pan ?? "",
      email: client?.email ?? "",
      phone_number: client?.phone_number ?? "",
    },
    resolver: zodResolver(UpdateClientSchema),
  });

  const updateClient = useUpdateClient(Number(id));

  const { mutateAsync: createInvestmentAccount } = useCreateInvestmentAccount();

  const [checkKyc, isCheckingKyc] = useCheckKyc(Number(id));

  const handleCheckKyc = useCallback(
    async (data: z.infer<typeof UpdateClientSchema>) => {
      try {
        setCachedClientName(client?.name || null);
        await checkKyc(data);
      } catch {
        toast.error("Failed to check KYC status");
      }
    },
    [checkKyc, client?.name]
  );

  const { dirtyFields, isSubmitting } = form.formState;

  const panIsDirty = !!dirtyFields.pan;
  const panValue = form.watch("pan");

  const invalidateClients = useInvalidateClients();

  const createUpdateAccount = async (
    data: z.infer<typeof UpdateClientSchema>
  ) => {
    try {
      // A minor's PAN was set by the guardian KYC check; the form doesn't show it
      const updated =
        isMinorAccount && client
          ? client
          : await updateClient.mutateAsync(data);
      if (action === "create-account") {
        await createInvestmentAccount(updated.id);
        await router.invalidate();
        invalidateClients();
        const portfolioPath = `/clients/${updated.id}/portfolio`;
        if (location.pathname !== portfolioPath) {
          navigate({
            to: "/clients/$id/portfolio",
            params: { id: updated.id.toString() },
            search: {},
          });
        }
      } else {
        toast.success("Updated client details");
      }
      onClose?.();
    } catch (error) {
      handleError(
        error,
        action === "create-account"
          ? "Failed to create account"
          : "Failed to update client"
      );
    }
  };

  const shouldCheckKyc =
    !client?.kyc_status ||
    client?.kyc_status === "U" ||
    panIsDirty ||
    !panValue ||
    isCheckingKyc;
  const showKycStatus = !shouldCheckKyc;
  const nameChanged =
    showKycStatus &&
    !!cachedClientName &&
    !!client?.name &&
    cachedClientName !== client.name;

  return (
    <div className="flex flex-col size-full gap-6 p-6">
      <h4>
        {action === "create-account"
          ? "Create account"
          : "Update client details"}
      </h4>
      <Form {...form}>
        <form className="flex flex-col flex-1 gap-4">
          <Activity mode={action !== "create-account" ? "visible" : "hidden"}>
            {/* Read-only: the account type can't be changed after creation */}
            <div className="flex flex-col gap-2">
              <V2Label>Account type</V2Label>
              <Select value={getAccountType(client)} disabled>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FormField
              name="phone_number"
              render={({ field }) => (
                <FormCountryCodePhoneInput
                  {...field}
                  // A minor's account uses the guardian's mobile and email
                  label={isMinor(client) ? "Guardian's mobile" : "Phone number"}
                  placeholder="9999999999"
                  value={field.value || "+91"}
                  allowCountryChange={isNRI(client)}
                />
              )}
            />
            <FormField
              name="name"
              render={({ field }) => (
                <FormInput placeholder="Enter name" label="Name" {...field} />
              )}
            />
          </Activity>
          {action === "create-account" ? (
            <div className="rounded-lg p-3 border border-border flex items-center gap-2">
              <Avatar fallback={getInitials(client?.name || "")} size="sm" />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold">{client?.name}</span>
                {client?.phone_number && (
                  <span className="text-xs text-muted-foreground">
                    {client.phone_number}
                  </span>
                )}
              </div>
              <ClientStatusBadge status={client?.status ?? "LEAD"} />
            </div>
          ) : null}
          {isMinorAccount ? (
            <MinorKycFields
              client={client}
              onVerifiedChange={setIsGuardianVerified}
              disabled={isSubmitting}
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-row items-start justify-between gap-2">
                <FormField
                  name="pan"
                  className="flex-1"
                  render={({ field }) => (
                    <FormInput
                      placeholder="ABCDE1234J"
                      label={
                        <>
                          <span>PAN</span>&nbsp;
                          {action !== "create-account" ? (
                            <span className="text-muted-foreground">
                              (Optional)
                            </span>
                          ) : null}
                        </>
                      }
                      {...field}
                      onChange={(val) =>
                        field.onChange(val?.toLocaleUpperCase() || "")
                      }
                    />
                  )}
                />
                {action === "create-account" ? (
                  <div className="shrink-0 pt-5">
                    {shouldCheckKyc ? (
                      <Button
                        variant="outline"
                        disabled={isCheckingKyc || !panValue || isSubmitting}
                        type="button"
                        onClick={form.handleSubmit(handleCheckKyc)}
                      >
                        <Loader2Icon
                          className={cn("size-4 animate-spin", {
                            hidden: !isCheckingKyc,
                          })}
                        />
                        Check KYC
                      </Button>
                    ) : (
                      <KycStatusBadge
                        kyc_status={client?.kyc_status}
                        remark={client?.kyc_status_remark}
                      />
                    )}
                  </div>
                ) : null}
              </div>
              {action === "create-account" && !shouldCheckKyc && (
                <KycDescription
                  client={client ?? null}
                  cachedName={nameChanged ? cachedClientName : undefined}
                  onCheckStatus={form.handleSubmit(handleCheckKyc)}
                  isCheckingStatus={isCheckingKyc}
                />
              )}
            </div>
          )}
          <Activity mode={action !== "create-account" ? "visible" : "hidden"}>
            <FormField
              name="email"
              render={({ field }) => (
                <FormInput
                  label={
                    <>
                      <span>
                        {isMinor(client) ? "Guardian's email" : "Email"}
                      </span>
                      &nbsp;
                      <span className="text-muted-foreground">(Optional)</span>
                    </>
                  }
                  {...field}
                  placeholder="Enter email address"
                />
              )}
            />
          </Activity>
          <DialogFooter className="mt-auto sm:flex-col gap-4">
            {action === "create-account" &&
              (isMinorAccount ? !isGuardianVerified : shouldCheckKyc) && (
                <div className="flex items-start gap-2 rounded-lg bg-tag-slate p-3 border border-border w-full">
                  <InfoIcon className="size-4 shrink-0 text-tag-slate-foreground" />
                  <span className="text-xs text-tag-slate-foreground">
                    Check KYC to create the account
                  </span>
                </div>
              )}
            <Button
              className="w-full"
              type="button"
              onClick={
                isMinorAccount
                  ? () => createUpdateAccount(form.getValues())
                  : form.handleSubmit(createUpdateAccount)
              }
              disabled={
                isCheckingKyc ||
                isSubmitting ||
                (isMinorAccount
                  ? !isGuardianVerified
                  : action === "create-account" && shouldCheckKyc)
              }
            >
              {action === "create-account" ? "Create Account" : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
